/**
 * The scripted coverage-gap demo: draw the Indian Ocean gap, fly there, then
 * add the Guam station and re-run so the blackout shrinks. Shared by the
 * Coverage gaps panel and the G shortcut.
 */
import { create } from 'zustand';
import { clockNow, useClock } from '@/state/store';
import { usePresenter } from '@/features/presenter/store';
import { AnimationTime } from '@/lib/animationTime';
import { toast } from '@/components/ui/toast';
import { GUAM } from '../constants';
import { getGlobeEngine } from '../globe/engineHandle';
import { useLoader } from '../store';
import type { Antenna } from '../types';

interface GuamDemoState {
  /** Blackout minutes per orbit shown by the counter. */
  blackout: number;
  setBlackout: (n: number) => void;
}

export const useGuamDemo = create<GuamDemoState>()((set) => ({
  blackout: GUAM.BLACKOUT_MINUTES,
  setBlackout: (blackout) => set({ blackout }),
}));

let counter: number | null = null;

export function startGuamDemo(): void {
  const engine = getGlobeEngine();
  if (!engine) {
    toast('warn', 'Open a globe mode first.');
    return;
  }
  useLoader.getState().setGuamDemoActive(true);
  useGuamDemo.getState().setBlackout(guamStationAdded() ? GUAM.BLACKOUT_WITH_GUAM : GUAM.BLACKOUT_MINUTES);
  engine.addCoverageGap({ ...GUAM.GAP });
  engine.flyTo(GUAM.CAMERA_TARGET.lon, GUAM.CAMERA_TARGET.lat, GUAM.CAMERA_TARGET.height, 2);
}

export function stopGuamDemo(): void {
  getGlobeEngine()?.removeCoverageGap(GUAM.GAP.name);
  useLoader.getState().setGuamDemoActive(false);
  if (counter) window.clearInterval(counter);
  counter = null;
  useGuamDemo.getState().setBlackout(GUAM.BLACKOUT_MINUTES);
}

export function toggleGuamDemo(): void {
  if (useLoader.getState().guamDemoActive) stopGuamDemo();
  else startGuamDemo();
}

export function guamStationAdded(): boolean {
  return useLoader.getState().dataset?.antennas.some((a) => a.id === GUAM.STATION_ID) ?? false;
}

/** Adds the 13 m Guam station to the network and animates the blackout counter down. */
export function addGuamStation(): void {
  const st = useLoader.getState();
  if (!st.dataset || guamStationAdded()) return;
  st.patchDataset({ antennas: [...st.dataset.antennas, GUAM.STATION as Antenna] });
  st.requestAnalysis();
  toast('success', 'Guam 13 m station added; re-running the analysis.');
  if (counter) window.clearInterval(counter);
  const time = new AnimationTime();
  time.sample(clockNow(), false);
  const start = useGuamDemo.getState().blackout;
  counter = window.setInterval(() => {
    const elapsed = time.sample(clockNow(), usePresenter.getState().frozen || !useClock.getState().playing);
    const b = Math.max(GUAM.BLACKOUT_WITH_GUAM, start - Math.floor(elapsed / 60));
    useGuamDemo.getState().setBlackout(b);
    if (b <= GUAM.BLACKOUT_WITH_GUAM) {
      if (counter) window.clearInterval(counter);
      counter = null;
      useGuamDemo.getState().setBlackout(GUAM.BLACKOUT_WITH_GUAM);
      return;
    }
  }, 100);
}
